import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  BookOpen, FileText, Inbox, Calendar, AlertCircle, CheckCircle2,
  TrendingUp, RefreshCw, ArrowRight, Lock, Unlock } from
'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'green' }) => {const { t } = useTranslation("finance");
  const colorClasses = {
    green: 'bg-green-500/20 text-green-500',
    blue: 'bg-blue-500/20 text-blue-500',
    amber: 'bg-amber-500/20 text-amber-500',
    purple: 'bg-purple-500/20 text-purple-500',
    red: 'bg-red-500/20 text-red-500'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>);

};

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

export const AccountingDashboard = () => {const { t } = useTranslation("finance");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/accounting/dashboard`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch dashboard');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err.message);
      setData({
        current_period: null,
        period_status: 'unknown',
        total_accounts: 0,
        pending_events: 0,
        posted_journals: 0,
        rejected_events: 0,
        period_summary: [],
        event_breakdown: [],
        recent_journals: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {fetchDashboard();}, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
      </div>);

  }

  const quickActions = [
  { name: 'Chart of Accounts', href: '/accounting/chart-of-accounts', icon: BookOpen },
  { name: 'Journal Entries', href: '/accounting/journals', icon: FileText },
  { name: 'Event Inbox', href: '/accounting/events', icon: Inbox },
  { name: 'Period Close', href: '/accounting/periods', icon: Calendar }];


  const periodStatusColor = {
    open: 'bg-green-500/20 text-green-400 border-green-500/30',
    soft_close: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    closed: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  return (
    <div className="space-y-6" data-testid="accounting-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("accountingDashboard", "Accounting Dashboard")}</h1>
          <p className="text-muted-foreground mt-1">{t("generalLedgerAnd", "General ledger and financial control")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}
          </Button>
          <Link to="/accounting/journals">
            <Button className="gap-2 bg-green-600 hover:bg-green-700">
              <FileText className="h-4 w-4" />{t("viewJournals", "View Journals")}
            </Button>
          </Link>
        </div>
      </div>

      {/* Current Period Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-blue-500/20">
                <Calendar className="h-8 w-8 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("currentPeriod", "Current Period")}</p>
                <p className="text-2xl font-bold">{data?.current_period || 'Not Set'}</p>
              </div>
            </div>
            <Badge className={periodStatusColor[data?.period_status] || periodStatusColor.open}>
              {data?.period_status === 'open' && <Unlock className="h-3 w-3 mr-1" />}
              {data?.period_status === 'closed' && <Lock className="h-3 w-3 mr-1" />}
              {data?.period_status?.replace('_', ' ') || 'Unknown'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Chart of Accounts" value={data?.total_accounts || 0} subtitle="Total accounts" icon={BookOpen} color="green" />
        <StatCard title="Pending Events" value={data?.pending_events || 0} subtitle="Awaiting processing" icon={Inbox} color="amber" />
        <StatCard title="Posted Journals" value={data?.posted_journals || 0} subtitle="This period" icon={FileText} color="blue" />
        <StatCard title="Rejected Events" value={data?.rejected_events || 0} subtitle="Needs attention" icon={AlertCircle} color="red" />
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-4">
        {quickActions.map((action) =>
        <Link key={action.name} to={action.href}>
            <Card className="hover:border-green-500 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <action.icon className="h-8 w-8 text-green-500 mb-3" />
                <h3 className="font-semibold">{action.name}</h3>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Event Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Inbox className="h-5 w-5 text-amber-500" />{t("eventProcessing", "Event Processing")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.event_breakdown || []).length === 0 ?
            <div className="text-center py-8">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="text-muted-foreground">{t("noEventsTo", "No events to process")}</p>
              </div> :

            <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                  data={data.event_breakdown}
                  cx="50%" cy="50%" outerRadius={80}
                  dataKey="count" nameKey="status"
                  label={({ name, value }) => `${name}: ${value}`}>
                  
                    {data.event_breakdown.map((_, index) =>
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  )}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            }
          </CardContent>
        </Card>

        {/* Recent Journals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />{t("recentJournals", "Recent Journals")}
            </CardTitle>
            <Link to="/accounting/journals">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}<ArrowRight className="h-4 w-4" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(data?.recent_journals || []).length === 0 ?
              <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">{t("noJournalEntries", "No journal entries yet")}</p>
                </div> :

              data.recent_journals.slice(0, 5).map((journal) =>
              <div key={journal.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div>
                      <p className="font-medium">{journal.journal_number || `#${journal.id}`}</p>
                      <p className="text-sm text-muted-foreground">{journal.posting_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${(journal.total_debit || 0).toLocaleString()}</p>
                      <Badge variant="secondary" className="text-xs">{journal.status}</Badge>
                    </div>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Close Checklist (if period is open) */}
      {data?.period_status === 'open' &&
      <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />{t("periodCloseChecklist", "Period Close Checklist")}
          </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-3">
                {data?.pending_events === 0 ?
              <CheckCircle2 className="h-5 w-5 text-green-500" /> :

              <AlertCircle className="h-5 w-5 text-amber-500" />
              }
                <span>{t("processAllEvents", "Process all events")}</span>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-3">
                {data?.rejected_events === 0 ?
              <CheckCircle2 className="h-5 w-5 text-green-500" /> :

              <AlertCircle className="h-5 w-5 text-red-500" />
              }
                <span>{t("resolveRejectedEvents", "Resolve rejected events")}</span>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                <span>{t("reviewTrialBalance", "Review trial balance")}</span>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                <span>{t("runStatutoryExports", "Run statutory exports")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      }
    </div>);

};

export default AccountingDashboard;